<script setup>
import CompanyLogo from '@/components/CompanyLogo.vue';
import KHKNotificationCard from '@/components/ui/KHKNotificationCard.vue';

import authService from '@/services/AuthService.js';
import { setPendingLoginBrand } from '@/composables/useSelectedBrand';
import store from '@/store';
import { useAuth } from '@okta/okta-vue';
import { Button, Select } from 'primevue';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const $auth = useAuth();

const errorMessage = ref('');
const loading = ref(false);

// Brand picker on the logged-out screen (#2122). The list is hardcoded because
// the brand-config endpoint requires auth. NEW BRANDS: add an entry here (code,
// name, playMode) — the authenticated switcher (SelectBrandView) picks new brands
// up from the backend automatically, but this logged-out list cannot. The choice
// is stashed for post-login persistence and re-skins the login page immediately.
const brands = [
    { code: 'giddyup', name: 'GiddyUp', playMode: 'real_money' },
    { code: 'casinox', name: 'CasinoX', playMode: 'sweepstakes' }
];
const selectedBrandCode = ref(store.state.selectedBrand?.code || 'giddyup');

function onBrandChange(event) {
    const brand = brands.find(b => b.code === event.value);
    if (!brand) return;
    setPendingLoginBrand(brand.code);
    // Re-skin the login/callback pages to the chosen brand right away. The
    // post-login hydration replaces this with the backend-resolved brand.
    store.commit('setSelectedBrand', { id: null, code: brand.code, name: brand.name, playMode: brand.playMode });
}

async function handleOktaLogin() {
    loading.value = true;
    errorMessage.value = '';

    try {
        console.log('Initiating Okta login redirect...');
        await $auth.signInWithRedirect();
    } catch (error) {
        console.error('Okta login error:', error);
        errorMessage.value = error.message || 'Failed to initiate login. Please try again.';
        loading.value = false;
    }
}

onMounted(async () => {
    // A pending brand is only meaningful for the login attempt it was picked in.
    // Clear any leftover from an abandoned attempt so it can't be silently applied
    // to whoever logs in next on this machine (#2122).
    setPendingLoginBrand(null);

    const isAuthenticated = await $auth.isAuthenticated();

    if (isAuthenticated || authService.isAuthenticated()) {
        console.log('User already authenticated, redirecting to dashboard');
        router.push({ name: 'dashboard' });
        return;
    }

});
</script>

<template>
    <div class="auth-screen flex items-center justify-center min-h-screen min-w-[100vw] overflow-hidden bg-surface-ground">
        <div class="flex flex-col items-center justify-center">
            <div
                style="border-radius: 56px; padding: 0.3rem; background: linear-gradient(180deg, var(--primary-color) 10%, rgba(33, 150, 243, 0) 30%)">
                <div class="w-full bg-surface-card py-20 px-8 sm:px-20" style="border-radius: 53px">
                    <div class="text-center mb-8 flex flex-col justify-center items-center gap-3">
                        <div class="auth-logo">
                            <CompanyLogo />
                        </div>
                        <div class="auth-subtitle">
                            Sign in to KHK Admin Portal
                        </div>
                    </div>

                    <div class="w-full max-w-[30rem] flex flex-col items-center gap-5">
                        <KHKNotificationCard v-if="errorMessage" :notification="{ message: errorMessage }" type="error"
                            class="w-full" />

                        <div class="brand-choice w-full">
                            <label class="brand-label">
                                <i class="pi pi-building"></i>
                                <span>Brand</span>
                            </label>
                            <Select v-model="selectedBrandCode" :options="brands" optionLabel="name" optionValue="code"
                                @change="onBrandChange" class="w-full auth-select"
                                overlayClass="auth-select-panel" />
                        </div>

                        <Button @click="handleOktaLogin" :loading="loading" :disabled="loading"
                            class="auth-button w-full">
                            <template #default>
                                <div class="button-content">
                                    <div class="okta-icon">
                                        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                            <path
                                                d="M12 0C5.389 0 0 5.389 0 12s5.389 12 12 12 12-5.389 12-12S18.611 0 12 0zm0 18c-3.314 0-6-2.686-6-6s2.686-6 6-6 6 2.686 6 6-2.686 6-6 6z" />
                                        </svg>
                                    </div>
                                    <span>{{ loading ? 'Redirecting...' : 'Continue with Okta' }}</span>
                                    <i v-if="!loading" class="bi bi-arrow-right"></i>
                                </div>
                            </template>
                        </Button>

                        <div class="security-note">
                            <i class="bi bi-lock-fill"></i>
                            <span>Secured by Okta Single Sign-On</span>
                        </div>
                    </div>
                </div>
            </div>

            <p class="auth-footer">
                © {{ new Date().getFullYear() }} KHK Games. All rights reserved.
            </p>
        </div>
    </div>
</template>

<style scoped>
/* Login-specific: the Okta button's inner content and the security note. Shared
   card/logo/select/button styling lives in assets/auth-screen.css. */
.button-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    color: white;
}

.okta-icon {
    display: flex;
    align-items: center;
    justify-content: center;
}

.button-content i {
    transition: transform 0.3s ease;
}

.auth-button:hover .button-content i {
    transform: translateX(4px);
}

.security-note {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    color: var(--text-color-secondary);
    font-size: 0.85rem;
    margin-top: 0.25rem;
}

.security-note i {
    font-size: 0.9rem;
}
</style>
